import React, { useMemo, useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { nl } from 'date-fns/locale';

const STATUS_DOT = {
  overdue: 'bg-red-500',
  due_soon: 'bg-orange-500',
  upcoming: 'bg-[#2E75B6]',
  completed: 'bg-green-600',
};

const STATUS_LABEL = {
  overdue: 'Te laat',
  due_soon: 'Binnenkort',
  upcoming: 'Aankomend',
  completed: 'Afgerond',
};

function getDateKey(dateStr) {
  try {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch {
    return '';
  }
}

export default function ComplianceCalendar({ items }) {
  const [month, setMonth] = useState(new Date());

  // Group items by date
  const dateMap = useMemo(() => {
    const map = {};
    (items || []).forEach(item => {
      const key = getDateKey(item.due_date);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    return map;
  }, [items]);

  // Get all dates that have items
  const markedDates = useMemo(() => {
    return Object.keys(dateMap).map(key => {
      const [y, m, d] = key.split('-').map(Number);
      return new Date(y, m - 1, d);
    });
  }, [dateMap]);

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-4" data-testid="compliance-calendar-card">
      <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Kalender</h3>
      <div data-testid="compliance-month-grid-calendar">
        <Calendar
          mode="single"
          month={month}
          onMonthChange={setMonth}
          locale={nl}
          className="w-full"
          modifiers={{
            marked: markedDates,
          }}
          modifiersClassNames={{
            marked: 'compliance-calendar-marked',
          }}
          components={{
            DayContent: ({ date }) => {
              const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              const dayItems = dateMap[key];

              if (!dayItems || dayItems.length === 0) {
                return <span>{date.getDate()}</span>;
              }

              // Get unique statuses for this day
              const statuses = [...new Set(dayItems.map(i => i.status))];

              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col items-center">
                      <span>{date.getDate()}</span>
                      <div className="flex gap-0.5 justify-center mt-0.5">
                        {statuses.slice(0, 4).map((st, idx) => (
                          <span
                            key={idx}
                            className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[st] || STATUS_DOT.upcoming}`}
                          />
                        ))}
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <div className="space-y-1">
                      {dayItems.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[item.status] || STATUS_DOT.upcoming}`} />
                          <span className="text-xs truncate">{item.title}</span>
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            },
          }}
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100" data-testid="compliance-calendar-legend">
        {Object.entries(STATUS_DOT).map(([key, dotClass]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${dotClass}`} />
            <span className="text-[10px] text-slate-500">{STATUS_LABEL[key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
